import {
  EffectHandler,
  EffectParams,
  SpellEffect,
  SpellDefinition,
} from '../types/spell.types';
import type { EffectStep, ConfigParams } from '../types/effect.types';
import type {
  BuffDefinition,
  BuffModifier,
  ScalarStatKey,
} from '../types/buff.types';
import { resolveBuffDef } from '../utils/buff.utils';
import { SPELL_REGISTRY } from '../data/spells.config';
import { DungeonEntity } from '../entities/DungeonEntity';
import { dungeonService } from '../services/DungeonService';
import { animationService } from '../services/AnimationService';
import { msToTicks } from '../utils/timing.utils';
import { tileWidth, tileHeight } from '../data/tileset.config';
import {
  getDirectionFromPositions,
  getTargetPosition,
  isTileBlocking,
  getConeCells,
  isSpawnableCell,
} from '../utils/map.utils';
import { mapService } from '../services/MapService';
import {
  INDEPENDENT_LOOT_POOLS,
  WEIGHTED_LOOT_POOLS,
  GRIMOIRE_POOL,
  rollBoostItemId,
} from '../data/loot-pools.config';
import {
  rollIndependentPool,
  rollWeightedPool,
} from '../utils/loot-roll.utils';
import { getTalentLootMultiplier } from '../services/LootService';
import { LocationEffectContext } from '../types/location.types';
import { openInterface } from '../services/InterfaceService';
import { useGameStore } from '../store/gameSlice';
import { audioService } from '../services/AudioService';
import { BUFF_REGISTRY } from '../data/buffs.config';
import { Element, DungeonEntityType } from '../types/enums';
import { spellExecutor } from '../services/SpellService';
import { zoneEffectService } from '../services/ZoneEffectService';
import { useInventoryStore } from '../store/inventorySlice';
import * as contentUnlock from '../services/ContentUnlockService';
import { CONDITION_CHECKERS } from './conditionChecker';
import {
  isAttackerBehindTarget,
  directionToVector,
} from '../utils/direction.utils';
import {
  applyMasteryBoost,
  learnSpellFromGrimoire,
} from '../services/SpellMasteryService';
import { emitEntityEvent } from '../utils/buff.utils';
import { challengeService } from '../services/ChallengeService';
import { LOCATION_CHALLENGE_CHEST } from '../data/locations.config';
import { dynamicPlacementService } from '../services/DynamicPlacementService';

// Assert loosely-typed config values to their expected runtime type.
const num = (p: EffectParams, key: string): number => p[key] as number;
const str = (p: EffectParams, key: string): string => p[key] as string;

/**
 * Builds the internal passive buff carried by a trinket level / talent node.
 * `params` is the inline buff body (behavioral fields only); `internal: true`
 * excludes it from the HUD and from related-card derivation.
 */
export function buildPassiveBuff(params: ConfigParams): BuffDefinition {
  const { id, name, ...rest } = params as unknown as BuffDefinition;
  return { ...rest, id, name, internal: true };
}

// VFX rotation (radians) per DirectionEnum value, base = RIGHT (east) = 0.
// Screen space is y-down so a positive angle rotates clockwise toward BOTTOM.
const DIRECTION_RADIANS = [
  -Math.PI / 2, // TOP
  -Math.PI / 4, // TOP-RIGHT
  0, // RIGHT
  Math.PI / 4, // BOTTOM-RIGHT
  Math.PI / 2, // BOTTOM
  (3 * Math.PI) / 4, // BOTTOM-LEFT
  Math.PI, // LEFT
  (-3 * Math.PI) / 4, // TOP-LEFT
];

function computeDamageMultiplier(
  caster: DungeonEntity,
  target: DungeonEntity,
): number {
  let multiplier = 1;
  for (const activeBuff of caster.getActiveBuffs()) {
    const dmcs = activeBuff.def.damageMultiplierConditions;
    if (!dmcs) {
      continue;
    }
    for (const dmc of dmcs) {
      const conditionsMatch =
        !dmc.conditions ||
        dmc.conditions.length === 0 ||
        dmc.conditions.every(
          (c) =>
            CONDITION_CHECKERS[c.check]?.(target, c.params, caster) ?? false,
        );
      if (!conditionsMatch) {
        continue;
      }
      if (dmc.requireBackstab && !isAttackerBehindTarget(caster, target)) {
        continue;
      }
      multiplier *= dmc.multiplier;
    }
  }
  return multiplier;
}

/**
 * Combined multiplier from the spell's per-run dynamic hooks (trinket redesign):
 * - A) dynamicDamageByBuffStacks — scales by the caster's current buff stacks.
 * - C) conditionalDamageMultipliers — TARGET-conditioned multipliers, evaluated
 *      like buff `damageMultiplierConditions` (caster passed for relational checks).
 * Returns 1 when the spell declares neither (existing spells unaffected).
 */
function computeSpellInstanceMultiplier(
  spell: SpellDefinition | undefined,
  caster: DungeonEntity,
  target: DungeonEntity,
): number {
  if (!spell) {
    return 1;
  }
  let multiplier = 1;
  const dyn = spell.dynamicDamageByBuffStacks;
  if (dyn) {
    const casterStacks =
      caster.getActiveBuffs().find((b) => b.def.id === dyn.buffId)?.stacks ?? 0;
    multiplier *= 1 + (dyn.perStackPercent / 100) * casterStacks;
  }
  const conds = spell.conditionalDamageMultipliers;
  if (conds) {
    for (const dmc of conds) {
      const conditionsMatch =
        !dmc.conditions ||
        dmc.conditions.length === 0 ||
        dmc.conditions.every(
          (c) =>
            CONDITION_CHECKERS[c.check]?.(target, c.params, caster) ?? false,
        );
      if (conditionsMatch) {
        multiplier *= dmc.multiplier;
      }
    }
  }
  return multiplier;
}

function computeTargetDamageTakenMultiplier(target: DungeonEntity): number {
  let multiplier = 1;
  for (const activeBuff of target.getActiveBuffs()) {
    if (activeBuff.def.damageTakenMultiplier !== undefined) {
      multiplier *= activeBuff.def.damageTakenMultiplier;
    }
    const dmcs = activeBuff.def.damageTakenMultiplierConditions;
    if (!dmcs) {
      continue;
    }
    for (const dmc of dmcs) {
      const conditionsMatch =
        !dmc.conditions || dmc.conditions.length === 0
          ? true
          : dmc.conditions.every((c) =>
              CONDITION_CHECKERS[c.check]?.(target, c.params),
            );
      if (conditionsMatch) {
        multiplier *= dmc.multiplier;
      }
    }
  }
  return multiplier;
}

function computeDodgeChance(target: DungeonEntity, isMelee: boolean): number {
  let chance = 0;
  for (const activeBuff of target.getActiveBuffs()) {
    if (activeBuff.def.dodgeChance === undefined) {
      continue;
    }
    if (activeBuff.def.dodgeMeleeOnly && !isMelee) {
      continue;
    }
    chance += activeBuff.def.dodgeChance;
  }
  return Math.min(1, chance);
}

function computeLifeStealPercent(caster: DungeonEntity): number {
  let percent = 0;
  for (const activeBuff of caster.getActiveBuffs()) {
    if (activeBuff.def.lifeStealPercent === undefined) {
      continue;
    }
    percent += activeBuff.def.lifeStealPercent;
  }
  return percent;
}

// Re-entrance guard for echo-strike — prevents basic-attack → castLinkedSpell
// → dealDamage → onAttack → castLinkedSpell infinite recursion.
let isEchoCasting = false;

const RANDOM_ELEMENTS: readonly Element[] = [
  'joy',
  'sadness',
  'anger',
  'fear',
  'surprise',
  'disgust',
];

const SET_SPELL_FLAG_ALLOWED: ReadonlySet<string> = new Set([
  'bypassDiagonalMiss',
  'pierce',
]);

// Stats whose lower value is the beneficial one (multipliers, floor at the
// good end). A negative modifier on these is a buff, not a debuff.
const LOWER_IS_BETTER_STATS: ReadonlySet<ScalarStatKey> = new Set([
  'spellCastTime',
  'spellCooldown',
]);

const isHarmfulModifier = (mod: BuffModifier): boolean => {
  const sign = LOWER_IS_BETTER_STATS.has(mod.stat) ? 1 : -1;
  return (mod.amount ?? 0) * sign > 0 || (mod.percent ?? 0) * sign > 0;
};

// see docs/buffs.md — harmful status (debuff). Explicit `harmful` overrides
// the mechanical inference below; drives the antidote cleanse.
const isHarmfulStatus = (def: BuffDefinition): boolean => {
  if (def.harmful !== undefined) {
    return def.harmful;
  }
  if (def.dot || def.stuns || (def.damageTakenMultiplier ?? 1) > 1) {
    return true;
  }
  return def.modifiers?.some(isHarmfulModifier) ?? false;
};

const pendingRecastTimers = new Set<ReturnType<typeof setTimeout>>();

export function cancelPendingRecasts(): void {
  for (const timerId of pendingRecastTimers) {
    clearTimeout(timerId);
  }
  pendingRecastTimers.clear();
}

export const EFFECT_HANDLERS: Record<string, EffectHandler> = {
  /**
   * Used exclusively by buff hooks — `_buff` is injected by emitEntityEvent.
   */
  removeSelf: (ctx, p) => {
    if (p._buff) {
      ctx.caster.removeBuff(p._buff);
    }
  },

  /**
   * Used exclusively by `ItemListener`s — `_itemSlot` is injected by
   * `emitItemListeners`.
   */
  consumeSelf: (_ctx, p) => {
    const slot = p._itemSlot as
      | { itemId: string; slotIndex: number }
      | undefined;
    if (slot) {
      useInventoryStore.getState().removeItem(slot.itemId, 1);
    }
  },

  /**
   * Skips silently if the target is blocked by a wall corner (diagonal cut
   * not allowed for this spell) — the spell still played its animation and
   * cooldown, but the hit doesn't connect.
   *
   * Pipeline (skipped on dodge):
   * - apply the linear mastery boost on the amount (player spells),
   * - roll dodge (cf. `dodgeChance` buffs),
   * - apply conditional multipliers (DTM caster + target),
   * - tag `lastDamageSourceSpellId` / `lastDamageSourceCasterId` on the
   *   target — read by the killer's `onKill` hook to compare the
   *   `linkedSpellId` of tracker buffs (mod-feast, mod-cyclic-death,
   *   mod-combustion, mod-rich-drop),
   * - deal damage via `dealPhysicalDamage` / `dealMagicDamage`,
   * - lifesteal on the **actually applied** damage (post defense/crit),
   * - gain linear mastery XP (player spells).
   */
  dealDamage: (ctx, p) => {
    if (ctx.targetX === undefined || ctx.targetY === undefined) {
      return;
    }
    ctx.caster.direction = ctx.direction;
    const entity = dungeonService.getEntityAt(
      ctx.targetX,
      ctx.targetY,
      ctx.caster.id,
    );
    if (entity) {
      const element = p.element as Element | undefined;
      const isPlayerSpell =
        ctx.spell?.mastery !== undefined &&
        ctx.caster.type === DungeonEntityType.PLAYER;
      let baseAmount = num(p, 'amount');
      if (isPlayerSpell) {
        baseAmount = applyMasteryBoost(ctx.caster, ctx.spell!.id, baseAmount);
      }

      const isMelee = (ctx.spell?.range ?? 1) <= 1;
      if (Math.random() < computeDodgeChance(entity, isMelee)) {
        return;
      }

      // Tag the source before computing the multiplier so conditions like
      // `currentSpellId` (damageMultiplierConditions) see the current spell.
      entity.lastDamageSourceSpellId = ctx.spell?.id;
      entity.lastDamageSourceCasterId = ctx.caster.id;
      const mult =
        computeDamageMultiplier(ctx.caster, entity) *
        computeTargetDamageTakenMultiplier(entity) *
        computeSpellInstanceMultiplier(ctx.spell, ctx.caster, entity);
      const finalAmount =
        mult !== 1 ? Math.round(baseAmount * mult) : baseAmount;
      const dealt =
        p.type === 'physical'
          ? ctx.caster.dealPhysicalDamage(entity, finalAmount, element)
          : ctx.caster.dealMagicDamage(entity, finalAmount, element);
      ctx.hitAny = true;

      const lifeStealPct =
        computeLifeStealPercent(ctx.caster) +
        ((p.lifeStealPercent as number | undefined) ?? 0);
      if (lifeStealPct > 0 && dealt > 0) {
        ctx.caster.gainHealth(Math.round(dealt * lifeStealPct));
      }
    }
  },

  /**
   * Same mechanics as `dealDamage` (per-target dodge, conditional DTM,
   * source tag for `onKill`, lifesteal on actually-applied damage, mastery
   * damage boost).
   *
   * If affectedCells is empty and params.radius is set, self-computes a
   * square of the given radius around the caster (radius 1 = 3×3 grid
   * including caster tile).
   *
   * Optional `falloffPerCell` applies a linear per-cell damage falloff by
   * cell index in the affectedCells order (distance-ordered for LINE).
   */
  dealDamageInCells: (ctx, p) => {
    let cells = ctx.affectedCells;
    if (cells.length === 0 && p.radius !== undefined) {
      const r = p.radius as number;
      const tmp: { x: number; y: number }[] = [];
      for (let dx = -r; dx <= r; dx++) {
        for (let dy = -r; dy <= r; dy++) {
          tmp.push({
            x: ctx.caster.positionX + dx,
            y: ctx.caster.positionY + dy,
          });
        }
      }
      cells = tmp;
    }
    const element = p.element as Element | undefined;
    const isPlayerSpell =
      ctx.spell?.mastery !== undefined &&
      ctx.caster.type === DungeonEntityType.PLAYER;
    let baseAmount = num(p, 'amount');
    if (isPlayerSpell) {
      baseAmount = applyMasteryBoost(ctx.caster, ctx.spell!.id, baseAmount);
    }
    const isMelee = (ctx.spell?.range ?? 1) <= 1;
    // Per-cell linear damage falloff by cell index (cells are kept in the
    // order produced by resolveAffectedCells — for LINE that is increasing
    // distance from the caster). falloffPerCell=0.75 → cell 0 = 100%,
    // cell 1 = 25%, cell 2 = 0%. Absent → no falloff (uniform damage).
    const falloffPerCell = (p.falloffPerCell as number | undefined) ?? 0;
    let hitAny = false;
    let totalLifeStealHeal = 0;

    let cellIndex = -1;
    for (const cell of cells) {
      cellIndex++;
      const entity = dungeonService.getEntityAt(cell.x, cell.y, ctx.caster.id);
      if (!entity) {
        continue;
      }

      if (Math.random() < computeDodgeChance(entity, isMelee)) {
        continue;
      }

      const falloffFactor =
        falloffPerCell > 0 ? Math.max(0, 1 - falloffPerCell * cellIndex) : 1;
      const cellBaseAmount =
        falloffFactor !== 1
          ? Math.round(baseAmount * falloffFactor)
          : baseAmount;
      // Tag the source before computing the multiplier so conditions like
      // `currentSpellId` see the current spell.
      entity.lastDamageSourceSpellId = ctx.spell?.id;
      entity.lastDamageSourceCasterId = ctx.caster.id;
      const mult =
        computeDamageMultiplier(ctx.caster, entity) *
        computeTargetDamageTakenMultiplier(entity) *
        computeSpellInstanceMultiplier(ctx.spell, ctx.caster, entity);
      const finalAmount =
        mult !== 1 ? Math.round(cellBaseAmount * mult) : cellBaseAmount;
      const dealt =
        p.type === 'physical'
          ? ctx.caster.dealPhysicalDamage(entity, finalAmount, element)
          : ctx.caster.dealMagicDamage(entity, finalAmount, element);
      hitAny = true;
      totalLifeStealHeal += dealt;
    }

    if (hitAny) {
      ctx.hitAny = true;
    }

    const lifeStealPct =
      computeLifeStealPercent(ctx.caster) +
      ((p.lifeStealPercent as number | undefined) ?? 0);
    if (lifeStealPct > 0 && totalLifeStealHeal > 0) {
      ctx.caster.gainHealth(Math.round(totalLifeStealHeal * lifeStealPct));
    }
  },

  /**
   * Deals flat damage, bypassing attack/defense stats and crit rolls, and
   * does not emit `onAttack`. For damage sources that should be independent
   * of the caster's offensive stats and the target's defenses (ex. thrown
   * explosives). If affectedCells is empty and params.radius is set,
   * self-computes a square of the given radius around the caster.
   */
  dealFixedDamageInCells: (ctx, p) => {
    let cells = ctx.affectedCells;
    if (cells.length === 0 && p.radius !== undefined) {
      const r = p.radius as number;
      const tmp: { x: number; y: number }[] = [];
      for (let dx = -r; dx <= r; dx++) {
        for (let dy = -r; dy <= r; dy++) {
          tmp.push({
            x: ctx.caster.positionX + dx,
            y: ctx.caster.positionY + dy,
          });
        }
      }
      cells = tmp;
    }
    const amount = num(p, 'amount');
    const element = p.element as Element | undefined;
    for (const cell of cells) {
      const entity = dungeonService.getEntityAt(cell.x, cell.y, ctx.caster.id);
      if (entity && entity.targetable) {
        let damage = amount;
        if (element) {
          damage = Math.floor(
            damage * DungeonEntity.computeElementalMultiplier(entity, element),
          );
        }
        entity.lastDamageSourceSpellId = ctx.spell?.id;
        entity.lastDamageSourceCasterId = ctx.caster.id;
        const wasAlreadyDead = entity.isDead();
        entity.takeDamage(damage, false, element);
        ctx.hitAny = true;
        if (!wasAlreadyDead && entity.isDead()) {
          emitEntityEvent(ctx.caster, 'onKill', {
            killSourceSpellId: ctx.spell?.id,
            eventPosX: entity.positionX,
            eventPosY: entity.positionY,
          });
        }
      }
    }
  },

  /**
   * - Targets with knockbackable=false are immune (no displacement, no stun, no cast cancel).
   * - On hit, the target's in-flight cast is cancelled.
   * - If pushed into a wall or another entity before reaching full distance,
   *   the target is stunned for `collisionStunMs` (internal default).
   * - `stepMs` overrides the per-tile animation duration (internal default).
   */
  knockback: (ctx, p) => {
    if (ctx.targetX === undefined || ctx.targetY === undefined) {
      return;
    }
    const entity = dungeonService.getEntityAt(
      ctx.targetX,
      ctx.targetY,
      ctx.caster.id,
    );
    if (!entity) {
      return;
    }
    const pushDir = getDirectionFromPositions(
      ctx.caster.positionX,
      ctx.caster.positionY,
      entity.positionX,
      entity.positionY,
    );
    entity.applyKnockbackAnimated(pushDir, num(p, 'distance'), {
      stepMs: p.stepMs as number | undefined,
      collisionStunMs: p.collisionStunMs as number | undefined,
    });
  },

  /**
   * Same rules as `knockback` (knockbackable + cancel-cast + stun-on-block).
   */
  knockbackAll: (ctx, p) => {
    const distance = num(p, 'distance');
    const stepMs = p.stepMs as number | undefined;
    const collisionStunMs = p.collisionStunMs as number | undefined;
    for (const cell of ctx.affectedCells) {
      const entity = dungeonService.getEntityAt(cell.x, cell.y, ctx.caster.id);
      if (!entity) {
        continue;
      }
      const pushDir = getDirectionFromPositions(
        ctx.caster.positionX,
        ctx.caster.positionY,
        entity.positionX,
        entity.positionY,
      );
      entity.applyKnockbackAnimated(pushDir, distance, {
        stepMs,
        collisionStunMs,
      });
    }
  },

  // Charge/blink: project along the cast direction up to the spell's range,
  // land on the last free cell before the first enemy or wall, and point the
  // follow-up damage at that enemy (if any). No enemy → dash to the farthest
  // free cell (no hit). cf. docs feedback: range-1 input no longer caps the dash.
  dashToTarget: (ctx) => {
    const range = ctx.spell?.range ?? 1;
    let cx = ctx.caster.positionX;
    let cy = ctx.caster.positionY;
    let landingX = cx;
    let landingY = cy;
    let enemyX: number | undefined;
    let enemyY: number | undefined;
    for (let i = 0; i < range; i++) {
      const next = getTargetPosition(cx, cy, ctx.direction);
      if (isTileBlocking(next.x, next.y)) {
        break;
      }
      if (dungeonService.locationService.isLocationBlockingAt(next.x, next.y)) {
        break;
      }
      if (dungeonService.getEntityAt(next.x, next.y, ctx.caster.id)) {
        enemyX = next.x;
        enemyY = next.y;
        break;
      }
      landingX = next.x;
      landingY = next.y;
      cx = next.x;
      cy = next.y;
    }
    if (
      landingX !== ctx.caster.positionX ||
      landingY !== ctx.caster.positionY
    ) {
      ctx.caster.positionX = landingX;
      ctx.caster.positionY = landingY;
      ctx.caster.absoluteX = landingX * tileWidth;
      ctx.caster.absoluteY = landingY * tileHeight;
      ctx.caster.markDirty();
    }
    ctx.targetX = enemyX ?? landingX;
    ctx.targetY = enemyY ?? landingY;
  },

  // Charge dash (distinct from dashToTarget's instant blink): a genuine
  // step-by-step move along the cast direction, one cell at a time (60 ms/cell,
  // chained via slideTo's onComplete). Each step re-checks the NEXT cell live —
  // an entity stepping into the lane mid-dash is respected. Stops on a wall or
  // the cell before the first occupied cell; only THEN fires the spell's
  // `impact`-phase effects (damage + on-hit, modifier-aware via the run clone)
  // and the impact VFX. An empty lane = no damage, no animation.
  dashLine: (ctx, p) => {
    const caster = ctx.caster;
    const dir = ctx.direction;
    const MS_PER_CELL = 60;
    const vfx = p.vfx as string | undefined;
    let remaining = num(p, 'cells');

    const hit = (ex: number, ey: number): void => {
      ctx.targetX = ex;
      ctx.targetY = ey;
      ctx.affectedCells = [{ x: ex, y: ey }];
      for (const eff of ctx.spell?.effects ?? []) {
        if ((eff.timing?.phase ?? 'action') === 'impact') {
          EFFECT_HANDLERS[eff.handler]?.(ctx, { ...eff.params });
        }
      }
      if (vfx) {
        animationService.createAnimation(vfx, ex, ey, msToTicks(300), {
          scale: 0.8,
        });
      }
    };

    const step = (): void => {
      if (remaining <= 0) {
        return;
      }
      const next = getTargetPosition(caster.positionX, caster.positionY, dir);
      if (isTileBlocking(next.x, next.y)) {
        return;
      }
      if (dungeonService.locationService.isLocationBlockingAt(next.x, next.y)) {
        return;
      }
      // A charge is a movement, not a teleport: it obeys the wall-corner rule
      // like walking does. A diagonal step cutting a wall corner stops the dash
      // (no hit) — unlike blink (`dashToTarget`), which passes through.
      if (
        !mapService.isDiagonalValid(
          caster.positionX,
          caster.positionY,
          next.x,
          next.y,
        )
      ) {
        return;
      }
      if (dungeonService.getEntityAt(next.x, next.y, caster.id)) {
        hit(next.x, next.y);
        return;
      }
      remaining -= 1;
      caster.slideTo(next.x, next.y, MS_PER_CELL, step);
    };
    step();
  },

  hitscan: (ctx, p) => {
    const caster = ctx.caster;
    const range = num(p, 'range');
    const triggerOnMiss = p.triggerOnMiss === true;
    const vfx = p.vfx as string | undefined;

    const runImpact = (ex: number, ey: number): void => {
      ctx.targetX = ex;
      ctx.targetY = ey;
      ctx.affectedCells = [{ x: ex, y: ey }];
      for (const eff of ctx.spell?.effects ?? []) {
        if ((eff.timing?.phase ?? 'action') === 'impact') {
          EFFECT_HANDLERS[eff.handler]?.(ctx, { ...eff.params });
        }
      }
      if (vfx) {
        animationService.createAnimation(vfx, ex, ey, msToTicks(300), {
          scale: 0.8,
        });
      }
    };

    let cx = caster.positionX;
    let cy = caster.positionY;
    for (let i = 0; i < range; i++) {
      const next = getTargetPosition(cx, cy, ctx.direction);
      if (!mapService.isWalkable(next.x, next.y, true)) {
        break;
      }
      if (dungeonService.getEntityAt(next.x, next.y, caster.id)) {
        runImpact(next.x, next.y);
        return;
      }
      cx = next.x;
      cy = next.y;
    }
    if (
      triggerOnMiss &&
      ctx.targetX !== undefined &&
      ctx.targetY !== undefined
    ) {
      runImpact(ctx.targetX, ctx.targetY);
    }
  },

  teleportRandom: (ctx, p) => {
    const radius = p.radius as number | undefined;
    let dest: { x: number; y: number } | null = null;
    if (radius && radius > 0) {
      const candidates: { x: number; y: number }[] = [];
      for (let dx = -radius; dx <= radius; dx++) {
        for (let dy = -radius; dy <= radius; dy++) {
          if (dx === 0 && dy === 0) {
            continue;
          }
          const nx = ctx.caster.positionX + dx;
          const ny = ctx.caster.positionY + dy;
          if (
            !isTileBlocking(nx, ny) &&
            !dungeonService.getEntityAt(nx, ny, ctx.caster.id)
          ) {
            candidates.push({ x: nx, y: ny });
          }
        }
      }
      if (candidates.length > 0) {
        dest = candidates[Math.floor(Math.random() * candidates.length)];
      }
    } else {
      // No radius: teleport anywhere on the floor (random walkable cell).
      dest = dungeonService.monsterSpawningService.findValidSpawnPosition();
    }
    if (!dest) {
      return;
    }
    ctx.caster.teleportTo(dest.x, dest.y);
    // Player only: the teleport bypasses `onPositionChange`, so replicate its
    // map housekeeping — snap the camera (no slide across the level) and reveal
    // fog around the arrival cell (otherwise the minimap stays blank there
    // until the next step). The camera follows the player.
    if (ctx.caster.type === DungeonEntityType.PLAYER) {
      dungeonService.requestCameraSnap();
      mapService.revealAround(dest.x, dest.y, ctx.caster.stats.viewRange);
    }
  },

  teleport: (ctx, p) => {
    const distance = num(p, 'distance');
    const ignoreObstacles = (p.ignoreObstacles as boolean | undefined) ?? false;
    const caster = ctx.caster;
    const { dx, dy } = directionToVector(caster.direction);
    let targetX = caster.positionX;
    let targetY = caster.positionY;
    for (let step = 1; step <= distance; step++) {
      const nx = caster.positionX + dx * step;
      const ny = caster.positionY + dy * step;
      if (isTileBlocking(nx, ny)) {
        break;
      }
      if (!ignoreObstacles) {
        const blocker = dungeonService.getEntityAt(nx, ny, caster.id);
        if (blocker && !blocker.isWalkableOver) {
          break;
        }
      }
      targetX = nx;
      targetY = ny;
    }
    caster.teleportTo(targetX, targetY);
    if (caster.type === DungeonEntityType.PLAYER) {
      mapService.revealAround(targetX, targetY, caster.stats.viewRange);
    }
  },

  setCasterState: (ctx, p) => {
    const MUTABLE_KEYS: ReadonlySet<string> = new Set([
      'targetable',
      'isWalkableOver',
      'animated',
    ]);
    const caster = ctx.caster as unknown as Record<string, unknown>;
    for (const [key, value] of Object.entries(p)) {
      if (key.startsWith('_') || !MUTABLE_KEYS.has(key)) {
        continue;
      }
      caster[key] = value;
    }
  },

  /**
   * Cancels any in-flight cast immediately. Typically used in
   * `interruptible.onInterrupt` to punish a failed spell (ex. parasite-void-cry).
   */
  applyStun: (ctx, p) => {
    ctx.caster.applyStun(msToTicks(num(p, 'durationMs')));
  },

  healShield: (ctx, p) => {
    // `requiresHit`: only regen if a damage effect connected earlier this phase
    // (e.g. baseball-glove basic attack heals shield only when it hits someone).
    if (p.requiresHit && !ctx.hitAny) {
      return;
    }
    const amount = p.full
      ? ctx.caster.maxShield - ctx.caster.shield
      : num(p, 'amount');
    if (amount > 0) {
      ctx.caster.gainShield(amount);
    }
  },

  healHP: (ctx, p) => {
    ctx.caster.gainHealth(num(p, 'amount'));
  },

  /**
   * Used by the `antidote` item (B8 §11). Removes every harmful status from
   * the user — `harmful` flag when set, otherwise inferred from mechanics
   * (see `isHarmfulStatus`).
   */
  cureNegativeStatus: (ctx) => {
    const active = ctx.caster.getActiveBuffs();
    for (let i = active.length - 1; i >= 0; i--) {
      if (isHarmfulStatus(active[i].def)) {
        ctx.caster.removeBuff(active[i]);
      }
    }
  },

  /**
   * Align the `timing` with the same spell's `playVfx` to sync audio and
   * visuals.
   */
  playSound: (_ctx, p) => {
    audioService.playSfx(str(p, 'id'), p.variant as string | undefined);
  },

  /**
   * - `onCaster: true` plays the VFX on the caster's tile.
   * - `onAffectedCells: true` plays the VFX on **every** shape cell (LINE,
   *   CROSS, CIRCLE, etc.). Otherwise the VFX plays on the target tile.
   */
  playVfx: (ctx, p) => {
    const name = str(p, 'name');
    const durationTicks = msToTicks(num(p, 'durationMs'));
    // `scaleTiles` renders the VFX that many tiles wide/tall (centered, anchor
    // 0.5) instead of the default grid cell size — used to blow up an AOE
    // effect over its whole zone.
    const scaleTiles = p.scaleTiles as number | undefined;
    const size =
      scaleTiles !== undefined
        ? { width: scaleTiles * tileWidth, height: scaleTiles * tileHeight }
        : {};
    // `rotate` spins the VFX toward the cast direction (base sprite faces RIGHT).
    const rotation = p.rotate
      ? (DIRECTION_RADIANS[ctx.direction] ?? 0)
      : undefined;
    const options = {
      originId: ctx.caster.id,
      direction: p.useDirection ? ctx.direction : undefined,
      isLooping: p.isLooping as boolean | undefined,
      rotation,
      scale: p.scale as number | undefined,
      ...size,
    };
    if (p.onAffectedCells) {
      for (const cell of ctx.affectedCells) {
        animationService.createAnimation(
          name,
          cell.x,
          cell.y,
          durationTicks,
          options,
        );
      }
      return;
    }
    const x = p.onCaster
      ? ctx.caster.positionX
      : (ctx.targetX ?? ctx.caster.positionX);
    const y = p.onCaster
      ? ctx.caster.positionY
      : (ctx.targetY ?? ctx.caster.positionY);
    animationService.createAnimation(name, x, y, durationTicks, options);
  },

  coneProgressiveDamage: (ctx, p) => {
    if (ctx.targetX === undefined || ctx.targetY === undefined) {
      return;
    }
    const amount = num(p, 'amount');
    const vfx = p.vfx as string | undefined;
    const coneRange = ctx.spell?.shapes?.[0]?.range ?? 3;
    const { orderedCells } = getConeCells(
      ctx.targetX,
      ctx.targetY,
      ctx.direction,
      coneRange,
      true,
    );

    const actionDuration = msToTicks(ctx.spell?.timings.actionMs ?? 0);
    const rangeDelay = actionDuration / (orderedCells.length || 1);

    orderedCells.forEach((rangeCells, rangeIndex) => {
      const delay = rangeDelay * (rangeIndex + 1);
      setTimeout(
        () => {
          if (
            ctx.caster.isDead() ||
            ctx.caster.getActionState()?.name === 'death'
          ) {
            return;
          }
          rangeCells.forEach((cell) => {
            if (vfx) {
              animationService.createAnimation(
                vfx,
                cell.x,
                cell.y,
                actionDuration / 3,
                { originId: ctx.caster.id, animationSpeed: 'auto' },
              );
            }
            const entity = dungeonService.getEntityAt(
              cell.x,
              cell.y,
              ctx.caster.id,
            );
            if (entity) {
              entity.lastDamageSourceSpellId = ctx.spell?.id;
              entity.lastDamageSourceCasterId = ctx.caster.id;
              if (p.type === 'physical') {
                ctx.caster.dealPhysicalDamage(entity, amount);
              } else {
                ctx.caster.dealMagicDamage(entity, amount);
              }
            }
          });
        },
        delay * (1000 / 60),
      ); // convert ticks to ms
    });
  },

  damageAllRoom: (ctx, p) => {
    const amount = num(p, 'amount');
    const entities = dungeonService.getAllEntities();
    for (const entity of entities) {
      if (entity.id !== ctx.caster.id) {
        entity.lastDamageSourceSpellId = ctx.spell?.id;
        entity.lastDamageSourceCasterId = ctx.caster.id;
        if (p.type === 'physical') {
          ctx.caster.dealPhysicalDamage(entity, amount);
        } else {
          ctx.caster.dealMagicDamage(entity, amount);
        }
      }
    }
  },

  /**
   * Refresh-or-add by id: a single live instance at a time. `durationMs`
   * overrides the BuffDefinition's `durationMs`.
   */
  applyBuff: (ctx, p) => {
    const buffId = str(p, 'buffId');
    const def = resolveBuffDef(ctx.caster, buffId);
    if (!def) {
      return;
    }
    const overrideMs = p.durationMs as number | undefined;
    const durationTicks =
      overrideMs !== undefined
        ? msToTicks(overrideMs)
        : def.durationMs !== undefined
          ? msToTicks(def.durationMs)
          : undefined;
    const existing = ctx.caster
      .getActiveBuffs()
      .find((b) => b.def.id === buffId);
    if (existing) {
      existing.remainingTicks = durationTicks;
    } else {
      ctx.caster.addBuff({ def, remainingTicks: durationTicks });
    }
  },

  /**
   * Grants the caster an internal passive buff built from FLAT params (id,
   * name, hooks, modifiers, damageMultiplierConditions, ...). Idempotent by id:
   * replaces the def in place on re-apply so the values stay current with the
   * trinket level / talent rank. Shared by trinkets and talents.
   */
  grantPassive: (ctx, params) => {
    const def = buildPassiveBuff(params as ConfigParams);
    const existing = ctx.caster
      .getActiveBuffs()
      .find((b) => b.def.id === def.id);
    if (existing) {
      existing.def = def;
    } else {
      ctx.caster.addBuff({ def, remainingTicks: undefined });
    }
  },

  /**
   * `targetTeam`: team filter relative to the caster.
   *   'opposite' (default) — opposite-type entities only (ex. demon-roar).
   *   'same'               — same-type entities only (ex. watcher-mark-of-gaze).
   *   'all'                — no filter (the caster stays excluded via getEntityAt).
   * `durationMs` overrides the BuffDefinition's `durationMs`.
   */
  applyBuffInNeighborsCells: (ctx, p) => {
    const buffId = str(p, 'buffId');
    const def = resolveBuffDef(ctx.caster, buffId);
    if (!def) {
      return;
    }
    const radius = (p.radius as number | undefined) ?? 1;
    const overrideMs = p.durationMs as number | undefined;
    const durationTicks =
      overrideMs !== undefined
        ? msToTicks(overrideMs)
        : def.durationMs !== undefined
          ? msToTicks(def.durationMs)
          : undefined;
    const targetTeam = (p.targetTeam ?? 'opposite') as
      | 'opposite'
      | 'same'
      | 'all';
    const visited = new Set<string>();
    for (const cell of ctx.affectedCells) {
      visited.add(`${cell.x},${cell.y}`);
    }
    for (const cell of ctx.affectedCells) {
      for (let dx = -radius; dx <= radius; dx++) {
        for (let dy = -radius; dy <= radius; dy++) {
          if (dx === 0 && dy === 0) {
            continue;
          }
          const nx = cell.x + dx;
          const ny = cell.y + dy;
          const key = `${nx},${ny}`;
          if (visited.has(key)) {
            continue;
          }
          visited.add(key);
          const entity = dungeonService.getEntityAt(nx, ny, ctx.caster.id);
          if (!entity) {
            continue;
          }
          if (targetTeam === 'opposite' && entity.type === ctx.caster.type) {
            continue;
          }
          if (targetTeam === 'same' && entity.type !== ctx.caster.type) {
            continue;
          }
          const existing = entity
            .getActiveBuffs()
            .find((b) => b.def.id === buffId);
          if (existing) {
            existing.remainingTicks = durationTicks;
          } else {
            entity.addBuff({ def, remainingTicks: durationTicks });
          }
        }
      }
    }
  },

  applyBuffInCells: (ctx, p) => {
    const buffId = str(p, 'buffId');
    const def = resolveBuffDef(ctx.caster, buffId);
    if (!def) {
      return;
    }
    const overrideMs = p.durationMs as number | undefined;
    const durationTicks =
      overrideMs !== undefined
        ? msToTicks(overrideMs)
        : def.durationMs !== undefined
          ? msToTicks(def.durationMs)
          : undefined;
    const targetTeam = (p.targetTeam ?? 'opposite') as
      | 'opposite'
      | 'same'
      | 'all';
    for (const cell of ctx.affectedCells) {
      const entity = dungeonService.getEntityAt(cell.x, cell.y, ctx.caster.id);
      if (!entity) {
        continue;
      }
      if (targetTeam === 'opposite' && entity.type === ctx.caster.type) {
        continue;
      }
      if (targetTeam === 'same' && entity.type !== ctx.caster.type) {
        continue;
      }
      const existing = entity.getActiveBuffs().find((b) => b.def.id === buffId);
      if (existing) {
        existing.remainingTicks = durationTicks;
      } else {
        entity.addBuff({ def, remainingTicks: durationTicks });
      }
    }
  },

  applyBuffWithChance: (ctx, p) => {
    const chance = num(p, 'chance');
    if (Math.random() * 100 >= chance) {
      return;
    }
    const { chance: _chance, ...rest } = p;
    void _chance;
    EFFECT_HANDLERS.applyBuffInCells(ctx, rest as EffectParams);
  },

  /**
   * Increments a stackable buff on the caster (creating it at `stacks =
   * amount` if absent), capped by `def.maxStacks`. `amount` defaults to 1.
   */
  addBuffStack: (ctx, p) => {
    const buffId = str(p, 'buffId');
    const amount = (p.amount as number | undefined) ?? 1;
    const def = resolveBuffDef(ctx.caster, buffId);
    if (!def) {
      return;
    }
    const cap = def.maxStacks ?? 1;
    const buffs = ctx.caster.getActiveBuffs();
    const existing = buffs.find((b) => b.def.id === buffId);
    let newStacks: number;
    if (existing) {
      newStacks = Math.min(cap, (existing.stacks ?? 1) + amount);
      existing.stacks = newStacks;
      if (def.durationMs !== undefined) {
        existing.remainingTicks = msToTicks(def.durationMs);
      }
    } else {
      newStacks = Math.min(cap, amount);
      ctx.caster.addBuff({
        def,
        remainingTicks:
          def.durationMs !== undefined ? msToTicks(def.durationMs) : undefined,
        stacks: newStacks,
      });
    }
    if (newStacks >= cap && def.onMaxStacks) {
      const ctxMax = { caster: ctx.caster, direction: 0, affectedCells: [] };
      for (const step of def.onMaxStacks) {
        const handler = EFFECT_HANDLERS[step.handler];
        if (handler) {
          handler(ctxMax, { ...step.params });
        }
      }
    }
  },

  addBuffStackInCells: (ctx, p) => {
    const buffId = str(p, 'buffId');
    const amount = (p.amount as number | undefined) ?? 1;
    const def = resolveBuffDef(ctx.caster, buffId);
    if (!def) {
      return;
    }
    const cap = def.maxStacks ?? 1;
    for (const cell of ctx.affectedCells) {
      const entity = dungeonService.getEntityAt(cell.x, cell.y, ctx.caster.id);
      if (!entity) {
        continue;
      }
      const buffs = entity.getActiveBuffs();
      const existing = buffs.find((b) => b.def.id === buffId);
      let newStacks: number;
      if (existing) {
        newStacks = Math.min(cap, (existing.stacks ?? 1) + amount);
        existing.stacks = newStacks;
        if (def.durationMs !== undefined) {
          existing.remainingTicks = msToTicks(def.durationMs);
        }
      } else {
        newStacks = Math.min(cap, amount);
        entity.addBuff({
          def,
          remainingTicks:
            def.durationMs !== undefined
              ? msToTicks(def.durationMs)
              : undefined,
          stacks: newStacks,
        });
      }
      if (newStacks >= cap && def.onMaxStacks) {
        const ctxMax = { caster: entity, direction: 0, affectedCells: [] };
        for (const step of def.onMaxStacks) {
          const handler = EFFECT_HANDLERS[step.handler];
          if (handler) {
            handler(ctxMax, { ...step.params });
          }
        }
      }
    }
  },

  clearBuffStack: (ctx, p) => {
    const buffId = str(p, 'buffId');
    const buffs = ctx.caster.getActiveBuffs();
    const buff = buffs.find((b) => b.def.id === buffId);
    if (buff) {
      ctx.caster.removeBuff(buff);
    }
  },

  // Run-start config: mutate the run-scoped buff instance so later
  // applications carry the scaled values.
  addRunBuffDot: (ctx, p) => {
    const b = ctx.caster.ensureBuffInstance(str(p, 'buffId'));
    if (b?.dot) {
      b.dot.amount += num(p, 'amount');
    }
  },
  addRunBuffMaxStacks: (ctx, p) => {
    const b = ctx.caster.ensureBuffInstance(str(p, 'buffId'));
    if (b) {
      b.maxStacks = (b.maxStacks ?? 1) + num(p, 'amount');
    }
  },

  /**
   * Coexists with the automatic SpellDefinition.preview system.
   */
  showPreview: (ctx, p) => {
    ctx.caster.createAffectedTiles(ctx.affectedCells, {
      duration: msToTicks(num(p, 'durationMs')),
      type: str(p, 'type'),
      color: num(p, 'color'),
      alpha: num(p, 'alpha'),
    });
  },

  /**
   * Different from a buff: the change persists for the entity's lifetime.
   */
  setStats: (ctx, p) => {
    (ctx.caster.baseStats as unknown as Record<string, unknown>)[
      str(p, 'stat')
    ] = num(p, 'value');
    ctx.caster.recomputeStats();
  },

  healHPPercent: (ctx, p) => {
    ctx.caster.gainHealth(Math.floor(ctx.caster.maxLife * num(p, 'percent')));
  },

  /**
   * Inflict damage directly on the caster (used for walk-type traps).
   */
  dealSelfDamage: (ctx, p) => {
    ctx.caster.lastDamageSourceSpellId = ctx.spell?.id;
    ctx.caster.lastDamageSourceCasterId = ctx.caster.id;
    if (p.type === 'physical') {
      ctx.caster.dealPhysicalDamage(ctx.caster, num(p, 'amount'));
    } else {
      ctx.caster.dealMagicDamage(ctx.caster, num(p, 'amount'));
    }
  },

  /**
   * ctx must be a LocationEffectContext.
   */
  markPoiUsed: (ctx) => {
    dungeonService.locationService.markLocationUsed(
      (ctx as LocationEffectContext).location,
    );
  },

  /**
   * ctx must be a LocationEffectContext.
   */
  triggerPoiBreakAnimation: (ctx) => {
    dungeonService.locationService.triggerLocationBreak(
      (ctx as LocationEffectContext).location,
    );
  },

  revealMap: () => {
    mapService.revealAll();
  },

  nextFloor: () => {
    dungeonService.emitGameEvent('nextFloor');
  },

  returnToHub: () => {
    dungeonService.emitGameEvent('returnToHub');
  },

  /**
   * Used by scrolls (cf. docs/items.md — scroll pattern). Bypasses cooldowns
   * and slot restrictions: a scroll is by construction one-shot and slotless.
   */
  castSpell: (ctx, p) => {
    const spellId = str(p, 'spellId');
    spellExecutor.execute(ctx.caster, spellId, ctx.targetX, ctx.targetY);
  },

  /**
   * Unlocks a spell in the persistent catalog (cf. `useSpellCatalogStore`).
   * Used by grimoires. If already unlocked, converts into mastery XP.
   */
  unlockSpell: (_ctx, p) => {
    const spellId = str(p, 'spellId');
    if (learnSpellFromGrimoire(spellId)) {
      useGameStore.getState().setSpellEquipPendingId(spellId);
      // Defer the interface swap so the inventory use-flow (useConsumable +
      // removeItem) finishes before the active interface changes.
      queueMicrotask(() => openInterface('spell-equip'));
    }
  },

  openInterface: (_ctx, p) => {
    openInterface(str(p, 'id'));
  },

  spawnMonsters: (_ctx, p) => {
    const count = num(p, 'count');
    const configId = str(p, 'monster');
    const level = dungeonService.getCurrentFloorLevel();
    for (let i = 0; i < count; i++) {
      const pos =
        dungeonService.monsterSpawningService.findValidSpawnPosition();
      if (!pos) {
        continue;
      }
      const monster = dungeonService.monsterSpawningService.createMonster(
        configId,
        pos.x,
        pos.y,
        level,
      );
      if (monster) {
        dungeonService.addEntity(monster);
      }
    }
  },

  spawnBiomeMonsters: (_ctx, p) => {
    const count = num(p, 'count');
    const biome = dungeonService.getCurrentBiome();
    if (!biome) {
      return;
    }
    for (let i = 0; i < count; i++) {
      const monsterId =
        dungeonService.monsterSpawningService.pickRandomMonsterIdForBiome(
          biome,
        );
      if (!monsterId) {
        continue;
      }
      const pos =
        dungeonService.monsterSpawningService.findValidSpawnPosition();
      if (!pos) {
        continue;
      }
      const monster = dungeonService.monsterSpawningService.createMonster(
        monsterId,
        pos.x,
        pos.y,
        dungeonService.monsterSpawningService.rollMonsterLevel(),
      );
      if (monster) {
        dungeonService.addEntity(monster);
      }
    }
  },

  unlockMinor: (_ctx, p) => {
    contentUnlock.unlockMinor(str(p, 'minorId'));
  },

  addSpellDamage: (ctx, p) => {
    const spellId = ctx.targetSpellId;
    if (!spellId) {
      return;
    }
    const amount = num(p, 'amount');
    const instance = ctx.caster.ensureSpellInstance(spellId);
    if (!instance) {
      return;
    }
    for (const eff of instance.effects) {
      if (eff.handler === 'dealDamage' && eff.params) {
        const params = eff.params as { amount?: number };
        params.amount = (params.amount ?? 0) + amount;
      }
    }
  },

  addSpellRange: (ctx, p) => {
    const spellId = ctx.targetSpellId;
    if (!spellId) {
      return;
    }
    const amount = num(p, 'amount');
    const instance = ctx.caster.ensureSpellInstance(spellId);
    if (!instance) {
      return;
    }
    // +range extends the spell's real reach: the delivery distance for delivery
    // spells (projectile/charge/hitscan), else the aim range for placement
    // spells. Zone size (shape.range) is a separate axis, left untouched.
    if (instance.projectile) {
      instance.projectile.range = (instance.projectile.range ?? 1) + amount;
      return;
    }
    const dash = instance.effects?.find((e) => e.handler === 'dashLine');
    if (dash) {
      dash.params.cells = num(dash.params, 'cells') + amount;
      return;
    }
    const hit = instance.effects?.find((e) => e.handler === 'hitscan');
    if (hit) {
      hit.params.range = num(hit.params, 'range') + amount;
      return;
    }
    instance.range = (instance.range ?? 0) + amount;
  },

  addSpellCooldownPercent: (ctx, p) => {
    const spellId = ctx.targetSpellId;
    if (!spellId) {
      return;
    }
    const percent = num(p, 'percent');
    const instance = ctx.caster.ensureSpellInstance(spellId);
    if (!instance) {
      return;
    }
    const current = instance.cooldownMs ?? 0;
    instance.cooldownMs = Math.round(current * (1 + percent / 100));
  },

  /**
   * Scales all timing phases of the spell's run instance by a percent
   * (mod-overclock). Negative percent = faster cast. Modifies the cloned
   * `timings` object on the run instance so `SpellService.buildTimings` picks
   * it up directly.
   */
  addSpellCastTimePercent: (ctx, p) => {
    const spellId = ctx.targetSpellId;
    if (!spellId) {
      return;
    }
    const percent = num(p, 'percent');
    const instance = ctx.caster.ensureSpellInstance(spellId);
    if (!instance) {
      return;
    }
    const factor = 1 + percent / 100;
    instance.timings = {
      ...instance.timings,
      castMs: Math.round(instance.timings.castMs * factor),
      actionMs: Math.round(instance.timings.actionMs * factor),
      lagMs: Math.round(instance.timings.lagMs * factor),
    };
  },

  addSpellEffectStep: (ctx, p) => {
    const spellId = ctx.targetSpellId;
    if (!spellId) {
      return;
    }
    const step = (p as { step?: EffectStep }).step;
    if (!step) {
      return;
    }
    const instance = ctx.caster.ensureSpellInstance(spellId);
    if (!instance) {
      return;
    }
    instance.effects.push({ ...step } as SpellEffect);
  },

  /**
   * Flat % damage boost on the socketed spell (mod-sharp-blade).
   */
  addSpellDamagePercent: (ctx, p) => {
    const spellId = ctx.targetSpellId;
    if (!spellId) {
      return;
    }
    const percent = num(p, 'percent');
    const instance = ctx.caster.ensureSpellInstance(spellId);
    if (!instance) {
      return;
    }
    const mult = 1 + percent / 100;
    for (const eff of instance.effects) {
      if (
        (eff.handler === 'dealDamage' ||
          eff.handler === 'dealDamageInCells' ||
          eff.handler === 'dealFixedDamageInCells') &&
        eff.params
      ) {
        const params = eff.params as { amount?: number };
        if (typeof params.amount === 'number') {
          params.amount = Math.round(params.amount * mult);
        }
      }
    }
  },

  /**
   * Per-run dynamic damage scaling by the caster's current buff stacks
   * (mixtape). Stored on the run instance, read at hit time by
   * `computeSpellInstanceMultiplier`.
   */
  scaleDamageByCasterBuffStacks: (ctx, p) => {
    if (!ctx.targetSpellId) {
      return;
    }
    const inst = ctx.caster.ensureSpellInstance(ctx.targetSpellId);
    if (inst) {
      inst.dynamicDamageByBuffStacks = {
        buffId: str(p, 'buffId'),
        perStackPercent: num(p, 'perStackPercent'),
      };
    }
  },

  /**
   * Per-run cooldown bypass at max stacks of a buff (earphones). Stored on the
   * run instance, read by `SpellService.execute`.
   */
  setSpellBypassCooldownAtMaxStacks: (ctx, p) => {
    if (!ctx.targetSpellId) {
      return;
    }
    const inst = ctx.caster.ensureSpellInstance(ctx.targetSpellId);
    if (inst) {
      inst.bypassCooldownAtMaxStacks = str(p, 'buffId');
    }
  },

  /**
   * Per-run spell-instance conditional damage multiplier (sheath). Stored on the
   * run instance, evaluated against the TARGET at hit time by
   * `computeSpellInstanceMultiplier`.
   */
  addSpellConditionalDamage: (ctx, p) => {
    if (!ctx.targetSpellId) {
      return;
    }
    const inst = ctx.caster.ensureSpellInstance(ctx.targetSpellId);
    if (!inst) {
      return;
    }
    inst.conditionalDamageMultipliers = inst.conditionalDamageMultipliers ?? [];
    inst.conditionalDamageMultipliers.push({
      multiplier: num(p, 'multiplier'),
      conditions: [
        { check: str(p, 'check'), params: { buffId: str(p, 'buffId') } },
      ],
    });
  },

  /**
   * Sets a boolean flag on the spell's run instance (mod-precise, mod-pierce).
   * Whitelisted to known flags. Special case: `pierce` is set on
   * `instance.projectile` (no-op if the spell has no projectile config).
   */
  setSpellFlag: (ctx, p) => {
    const spellId = ctx.targetSpellId;
    if (!spellId) {
      return;
    }
    const flag = str(p, 'flag');
    const value = p.value as boolean;
    if (!SET_SPELL_FLAG_ALLOWED.has(flag)) {
      return;
    }
    const instance = ctx.caster.ensureSpellInstance(spellId);
    if (!instance) {
      return;
    }
    if (flag === 'pierce') {
      if (!instance.projectile) {
        return;
      }
      instance.projectile.pierce = value;
      return;
    }
    (instance as unknown as Record<string, unknown>)[flag] = value;
  },

  /**
   * Applies a buff on the caster, storing `linkedSpellId =
   * ctx.targetSpellId`. Used by "tracker" modifiers that need to know which
   * socketed spell placed them (Mort-Cyclique, Festin, Drop-Riche, Combustion).
   */
  applySpellLinkedBuff: (ctx, p) => {
    const buffId = str(p, 'buffId');
    const linkedSpellId = ctx.targetSpellId;
    if (!linkedSpellId) {
      return;
    }
    const def = BUFF_REGISTRY[buffId];
    if (!def) {
      return;
    }
    const durationTicks =
      def.durationMs !== undefined ? msToTicks(def.durationMs) : undefined;
    const existing = ctx.caster
      .getActiveBuffs()
      .find((b) => b.def.id === buffId && b.linkedSpellId === linkedSpellId);
    if (existing) {
      existing.remainingTicks = durationTicks;
    } else {
      ctx.caster.addBuff({
        def,
        remainingTicks: durationTicks,
        linkedSpellId,
      });
    }
  },

  /**
   * Hook handler (Mort-Cyclique): if the kill comes from the spell linked to
   * the carrier buff, reduces that spell's cooldown. Reads
   * `_buff.linkedSpellId` and compares it to `ctx.killSourceSpellId`.
   */
  reduceLinkedSpellCooldown: (ctx, p) => {
    const buff = p._buff;
    if (!buff?.linkedSpellId) {
      return;
    }
    if (ctx.killSourceSpellId !== buff.linkedSpellId) {
      return;
    }
    const amountMs = num(p, 'amountMs');
    const state = ctx.caster.getSpellState(buff.linkedSpellId);
    state.cooldownRemaining = Math.max(
      0,
      state.cooldownRemaining - msToTicks(amountMs),
    );
  },

  /**
   * Hook handler (Festin): applies a target buff only if the kill comes from
   * the linked spell.
   */
  applyBuffIfLinkedSpell: (ctx, p) => {
    const buff = p._buff;
    if (!buff?.linkedSpellId) {
      return;
    }
    if (ctx.killSourceSpellId !== buff.linkedSpellId) {
      return;
    }
    const buffId = str(p, 'buffId');
    const def = BUFF_REGISTRY[buffId];
    if (!def) {
      return;
    }
    const durationTicks =
      def.durationMs !== undefined ? msToTicks(def.durationMs) : undefined;
    const existing = ctx.caster
      .getActiveBuffs()
      .find((b) => b.def.id === buffId);
    if (existing) {
      existing.remainingTicks = durationTicks;
    } else {
      ctx.caster.addBuff({ def, remainingTicks: durationTicks });
    }
  },

  /**
   * Hook handler (Combustion): spawns an AOE explosion at the event position
   * if the kill comes from the linked spell. Reads the sum of the linked
   * spell's dealDamage `amount`s as baseline and applies `damagePercent`.
   */
  spawnAoeOnLinkedKill: (ctx, p) => {
    const buff = p._buff;
    if (!buff?.linkedSpellId) {
      return;
    }
    if (ctx.killSourceSpellId !== buff.linkedSpellId) {
      return;
    }
    if (ctx.eventPosX === undefined || ctx.eventPosY === undefined) {
      return;
    }
    const radius = num(p, 'radius');
    const damagePercent = num(p, 'damagePercent');
    const instance =
      ctx.caster.ensureSpellInstance(buff.linkedSpellId) ??
      SPELL_REGISTRY[buff.linkedSpellId];
    if (!instance) {
      return;
    }
    let baseDamage = 0;
    let damageType: 'physical' | 'magic' = 'physical';
    let element: Element | undefined;
    for (const eff of instance.effects) {
      if (eff.handler === 'dealDamage' || eff.handler === 'dealDamageInCells') {
        const params = eff.params as {
          amount?: number;
          type?: 'physical' | 'magic';
          element?: Element;
        };
        baseDamage += params.amount ?? 0;
        if (params.type) {
          damageType = params.type;
        }
        if (params.element) {
          element = params.element;
        }
      }
    }
    const aoeAmount = Math.round(baseDamage * (damagePercent / 100));
    if (aoeAmount <= 0) {
      return;
    }
    const vfx = p.vfx as string | undefined;
    if (vfx) {
      const tiles = radius * 2 + 1;
      animationService.createAnimation(
        vfx,
        ctx.eventPosX,
        ctx.eventPosY,
        msToTicks(400),
        { scale: 0.8, width: tiles * tileWidth, height: tiles * tileHeight },
      );
    }
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        if (dx === 0 && dy === 0) {
          continue;
        }
        const x = ctx.eventPosX + dx;
        const y = ctx.eventPosY + dy;
        const target = dungeonService.getEntityAt(x, y, ctx.caster.id);
        if (!target || target.type === ctx.caster.type) {
          continue;
        }
        target.lastDamageSourceSpellId = buff.linkedSpellId;
        target.lastDamageSourceCasterId = ctx.caster.id;
        if (damageType === 'physical') {
          ctx.caster.dealPhysicalDamage(target, aoeAmount, element);
        } else {
          ctx.caster.dealMagicDamage(target, aoeAmount, element);
        }
      }
    }
  },

  /**
   * Hook handler (mod-echo-strike): fires on basic-attack (onAttack), casts
   * the spell linked to the carrier buff. Bypasses cooldown so the echo does
   * not consume or advance the spell's CD. Target = caster's direction × range.
   */
  castLinkedSpell: (ctx, p) => {
    if (isEchoCasting) {
      return;
    }
    const buff = p._buff;
    if (!buff?.linkedSpellId) {
      return;
    }
    const caster = ctx.caster;
    const spellId = buff.linkedSpellId;
    const def =
      ctx.caster.ensureSpellInstance(spellId) ?? SPELL_REGISTRY[spellId];
    if (!def) {
      return;
    }
    const { dx, dy } = directionToVector(caster.direction);
    const range = def.range ?? 1;
    const tx = caster.positionX + dx * range;
    const ty = caster.positionY + dy * range;
    const state = caster.getSpellState(spellId);
    const savedCd = state.cooldownRemaining;
    state.cooldownRemaining = 0;
    isEchoCasting = true;
    try {
      spellExecutor.execute(caster, def, tx, ty);
    } finally {
      isEchoCasting = false;
    }
    if (state.cooldownRemaining === 0) {
      state.cooldownRemaining = savedCd;
    }
  },

  /**
   * Schedules a re-cast of the linked spell after a delay, at a % of damage
   * (mod-fragile-chain). Bypasses the cooldown via direct re-execute.
   */
  scheduleSpellRecast: (ctx, p) => {
    if (!ctx.spell) {
      return;
    }
    const spellId = ctx.spell.id;
    const delayMs = num(p, 'delayMs');
    const damagePercent = num(p, 'damagePercent');
    const targetX = ctx.targetX;
    const targetY = ctx.targetY;
    const caster = ctx.caster;
    const timerId = setTimeout(() => {
      pendingRecastTimers.delete(timerId);
      if (caster.isDead()) {
        return;
      }
      const baseDef =
        ctx.caster.ensureSpellInstance(spellId) ?? SPELL_REGISTRY[spellId];
      if (!baseDef) {
        return;
      }
      const clone = JSON.parse(JSON.stringify(baseDef)) as SpellDefinition;
      // Strip self-replicating steps from the clone — they've already
      // fired their schedule/fan job ; re-running them would chain forever
      // (scheduleSpellRecast) or explode exponentially (castFanSpread).
      clone.effects = clone.effects.filter(
        (eff) =>
          eff.handler !== 'scheduleSpellRecast' &&
          eff.handler !== 'castFanSpread',
      );
      const mult = damagePercent / 100;
      for (const eff of clone.effects) {
        if (
          eff.handler === 'dealDamage' ||
          eff.handler === 'dealDamageInCells' ||
          eff.handler === 'dealFixedDamageInCells'
        ) {
          const params = eff.params as { amount?: number };
          if (typeof params.amount === 'number') {
            params.amount = Math.round(params.amount * mult);
          }
        }
      }
      // Bypass cooldown: temporarily set the spell state to 0.
      const state = caster.getSpellState(spellId);
      const savedCd = state.cooldownRemaining;
      state.cooldownRemaining = 0;
      spellExecutor.execute(caster, clone, targetX, targetY);
      // If execute didn't reset the cooldown (expected for player spells),
      // restore the old value so the CD isn't broken.
      if (state.cooldownRemaining === 0) {
        state.cooldownRemaining = savedCd;
      }
    }, delayMs);
    pendingRecastTimers.add(timerId);
  },

  /**
   * Pulls ground items within `radius` around the caster (mod-magnet).
   */
  pullItemsToward: (ctx, p) => {
    const radius = num(p, 'radius');
    if (ctx.caster.type !== DungeonEntityType.PLAYER) {
      return;
    }
    dungeonService.inventoryService.pickupItemsInRange(
      ctx.caster as unknown as import('../entities/PlayerEntity').PlayerEntity,
      radius,
    );
  },

  /**
   * Casts additional fan-spread copies (±angle) around the origin direction
   * (mod-triplet). Bypasses cooldown via direct re-execute. `count` =
   * number of additional casts (typically 2). `angleDegrees` = spread between
   * neighbors (typically 45°, i.e. ±1 on the 8 directions).
   */
  castFanSpread: (ctx, p) => {
    if (!ctx.spell) {
      return;
    }
    const spellId = ctx.spell.id;
    const count = num(p, 'count');
    const angleDegrees = num(p, 'angleDegrees');
    const damagePercent = num(p, 'damagePercent');
    const caster = ctx.caster;
    // On an 8-direction grid, 45° = ±1 step of DirectionEnum.
    const stepPerDir = Math.round(angleDegrees / 45);
    const baseDir = ctx.direction;
    // count = 2 → generates [-1, +1] × stepPerDir.
    const offsets: number[] = [];
    for (let i = 1; i <= Math.ceil(count / 2); i++) {
      offsets.push(-i * stepPerDir);
      if (offsets.length < count) {
        offsets.push(i * stepPerDir);
      }
    }
    const baseDef =
      ctx.caster.ensureSpellInstance(spellId) ?? SPELL_REGISTRY[spellId];
    if (!baseDef) {
      return;
    }
    const mult = damagePercent / 100;
    for (const offset of offsets) {
      const newDir = (((baseDir + offset) % 8) + 8) % 8;
      const clone = JSON.parse(JSON.stringify(baseDef)) as SpellDefinition;
      // Strip self-replicating steps — re-running them would explode
      // exponentially (castFanSpread) or chain forever (scheduleSpellRecast).
      clone.effects = clone.effects.filter(
        (eff) =>
          eff.handler !== 'castFanSpread' &&
          eff.handler !== 'scheduleSpellRecast',
      );
      if (mult !== 1) {
        for (const eff of clone.effects) {
          if (
            eff.handler === 'dealDamage' ||
            eff.handler === 'dealDamageInCells' ||
            eff.handler === 'dealFixedDamageInCells'
          ) {
            const params = eff.params as { amount?: number };
            if (typeof params.amount === 'number') {
              params.amount = Math.round(params.amount * mult);
            }
          }
        }
      }
      const range = ctx.spell.range ?? 1;
      const { dx, dy } = directionToVector(newDir);
      const tx = caster.positionX + dx * range;
      const ty = caster.positionY + dy * range;
      const state = caster.getSpellState(spellId);
      const savedCd = state.cooldownRemaining;
      state.cooldownRemaining = 0;
      spellExecutor.execute(caster, clone, tx, ty);
      if (state.cooldownRemaining === 0) {
        state.cooldownRemaining = savedCd;
      }
    }
  },

  /**
   * Rolls a random element and applies the matching
   * `mod-random-elemental-debuff-<element>` to opposite-type entities in
   * the affected cells (mod-random-elemental).
   */
  randomizeSpellElementApplyDebuff: (ctx) => {
    const element =
      RANDOM_ELEMENTS[Math.floor(Math.random() * RANDOM_ELEMENTS.length)];
    const buffId = `mod-random-elemental-debuff-${element}`;
    const def = BUFF_REGISTRY[buffId];
    if (!def) {
      return;
    }
    const durationTicks =
      def.durationMs !== undefined ? msToTicks(def.durationMs) : undefined;
    for (const cell of ctx.affectedCells) {
      const entity = dungeonService.getEntityAt(cell.x, cell.y, ctx.caster.id);
      if (!entity || entity.type === ctx.caster.type) {
        continue;
      }
      const existing = entity.getActiveBuffs().find((b) => b.def.id === buffId);
      if (existing) {
        existing.remainingTicks = durationTicks;
      } else {
        entity.addBuff({ def, remainingTicks: durationTicks });
      }
    }
  },

  /**
   * Spawns a persistent effect zone on the spell's affected cells
   * (mod-persistent-wave), delegated to `zoneEffectService`. `damagePercent`
   * is applied to the sum of the spell's dealDamage to set the per-tick amount.
   */
  spawnPersistentDamageZone: (ctx, p) => {
    const durationMs = num(p, 'durationMs');
    const intervalMs = num(p, 'intervalMs');
    const damagePercent = num(p, 'damagePercent');
    if (!ctx.spell || ctx.affectedCells.length === 0) {
      return;
    }
    let baseDamage = 0;
    let damageType: 'physical' | 'magic' = 'magic';
    let element: Element | undefined;
    for (const eff of ctx.spell.effects) {
      if (eff.handler === 'dealDamage' || eff.handler === 'dealDamageInCells') {
        const params = eff.params as {
          amount?: number;
          type?: 'physical' | 'magic';
          element?: Element;
        };
        baseDamage += params.amount ?? 0;
        if (params.type) {
          damageType = params.type;
        }
        if (params.element) {
          element = params.element;
        }
      }
    }
    const amount = Math.max(1, Math.round(baseDamage * (damagePercent / 100)));
    zoneEffectService.spawnZone({
      cells: ctx.affectedCells,
      durationMs,
      intervalMs,
      amount,
      type: damageType,
      element,
      caster: ctx.caster,
      sourceSpellId: ctx.spell.id,
    });
    // Visible footprint of the residual zone: static tint (no time-fill circle)
    // on each affected cell for the zone's lifetime.
    const previewColor = ctx.spell.preview?.color ?? 0xff5533;
    ctx.caster.createAffectedTiles(ctx.affectedCells, {
      duration: msToTicks(durationMs),
      alpha: 0.3,
      color: previewColor,
      noProgressFill: true,
    });
  },

  grantMinorTrinket: (_ctx, p) => {
    contentUnlock.unlockMinor(str(p, 'minorId'), { showcase: false });
  },

  enableFirstCastSkip: (_ctx, p) => {
    dungeonService.enableFirstCastSkip(num(p, 'amount'));
  },

  enableAggroReset: (ctx) => {
    const buffDef = BUFF_REGISTRY['aggro-reset'];
    if (!buffDef) {
      return;
    }
    const existing = ctx.caster
      .getActiveBuffs()
      .find((b) => b.def.id === 'aggro-reset');
    if (!existing) {
      ctx.caster.addBuff({ def: buffDef });
    }
  },

  /**
   * Drop items from a named loot pool, scattered on floor tiles within radius.
   * Stops after maxCount successful drops.
   * params: { pool: string, maxCount: number, radius: number }
   */
  // System 1 — independent per-entry chance + random quantity (chest, urn).
  spawnItemsFromPool: (ctx, p) => {
    const pool = str(p, 'pool');
    const maxCount = num(p, 'maxCount');
    const radius = num(p, 'radius');
    const lootTable = INDEPENDENT_LOOT_POOLS[pool];
    if (!lootTable) {
      return;
    }

    const cx = ctx.targetX ?? ctx.caster.positionX;
    const cy = ctx.targetY ?? ctx.caster.positionY;

    const groundTiles = mapService
      .getMapTiles()
      .filter(
        (t) =>
          Math.abs(t.x - cx) <= radius &&
          Math.abs(t.y - cy) <= radius &&
          isSpawnableCell(t.x, t.y, { allowOccupied: true }),
      );

    if (groundTiles.length === 0) {
      return;
    }

    const drops = rollIndependentPool(lootTable, {
      multiplier: getTalentLootMultiplier(),
      maxCount,
    });
    for (const drop of drops) {
      const tile = groundTiles[Math.floor(Math.random() * groundTiles.length)];
      dungeonService.inventoryService.dropItem(
        tile.x,
        tile.y,
        drop.itemId,
        drop.quantity,
      );
    }

    // Immediately pick up items that spawned at the player's feet
    const player = dungeonService.getPlayer();
    if (player) {
      dungeonService.inventoryService.pickupItemsInRange(
        player,
        player.stats.pickupReach,
      );
    }
  },

  // System 2 — X guaranteed weighted draws, capped per item (vault, challenge chest).
  spawnWeightedPool: (ctx, p) => {
    const pool = str(p, 'pool');
    const radius = num(p, 'radius');
    const weightedPool = WEIGHTED_LOOT_POOLS[pool];
    if (!weightedPool) {
      return;
    }

    const cx = ctx.targetX ?? ctx.caster.positionX;
    const cy = ctx.targetY ?? ctx.caster.positionY;

    const groundTiles = mapService
      .getMapTiles()
      .filter(
        (t) =>
          Math.abs(t.x - cx) <= radius &&
          Math.abs(t.y - cy) <= radius &&
          isSpawnableCell(t.x, t.y, { allowOccupied: true }),
      );

    if (groundTiles.length === 0) {
      return;
    }

    const drops = rollWeightedPool(weightedPool.entries, weightedPool.count, {
      multiplier: getTalentLootMultiplier(),
    });
    for (const drop of drops) {
      const tile = groundTiles[Math.floor(Math.random() * groundTiles.length)];
      dungeonService.inventoryService.dropItem(
        tile.x,
        tile.y,
        drop.itemId,
        drop.quantity,
      );
    }

    const player = dungeonService.getPlayer();
    if (player) {
      dungeonService.inventoryService.pickupItemsInRange(
        player,
        player.stats.pickupReach,
      );
    }
  },

  /**
   * Uniform draw from `GRIMOIRE_POOL`. If the matching spell is already
   * unlocked, the `unlockSpell` handler (triggered on grimoire use) grants
   * mastery XP fragments instead of unlocking. Sanctuaire-Grimoire effect (B8).
   */
  spawnRandomGrimoire: (ctx) => {
    const entries = GRIMOIRE_POOL.map((itemId) => ({
      itemId,
      weight: 1,
      max: 1,
    }));
    const grimoireId = rollWeightedPool(entries, 1)[0]?.itemId;
    if (!grimoireId) {
      return;
    }
    const x = ctx.targetX ?? ctx.caster.positionX;
    const y = ctx.targetY ?? ctx.caster.positionY;
    dungeonService.inventoryService.dropItem(x, y, grimoireId, 1);
    const player = dungeonService.getPlayer();
    if (player) {
      dungeonService.inventoryService.pickupItemsInRange(
        player,
        player.stats.pickupReach,
      );
    }
  },

  injectSpellEffectByElement: (ctx, p) => {
    const element = p.element as Element | undefined;
    const step = (p as { step?: SpellEffect }).step;
    if (!element || !step) {
      return;
    }
    for (const [spellId, spellDef] of Object.entries(SPELL_REGISTRY)) {
      const hasMatchingElement = spellDef.effects?.some((eff) => {
        const params = eff.params as { element?: string } | undefined;
        return (
          (eff.handler === 'dealDamage' ||
            eff.handler === 'dealDamageInCells') &&
          params?.element === element
        );
      });
      if (!hasMatchingElement) {
        continue;
      }
      const instance = ctx.caster.ensureSpellInstance(spellId);
      if (!instance) {
        continue;
      }
      instance.effects.push({ ...step } as SpellEffect);
    }
  },

  modifySpellByElement: (ctx, p) => {
    const element = p.element as Element | undefined;
    const step = (p as { step?: SpellEffect }).step;
    if (!element || !step) {
      return;
    }
    const handler = EFFECT_HANDLERS[step.handler];
    if (!handler) {
      return;
    }
    for (const [spellId, spellDef] of Object.entries(SPELL_REGISTRY)) {
      const hasMatchingElement = spellDef.effects?.some((eff) => {
        const params = eff.params as { element?: string } | undefined;
        return (
          (eff.handler === 'dealDamage' ||
            eff.handler === 'dealDamageInCells') &&
          params?.element === element
        );
      });
      if (!hasMatchingElement) {
        continue;
      }
      const subCtx = {
        caster: ctx.caster,
        direction: 0,
        affectedCells: [],
        targetSpellId: spellId,
      };
      handler(subCtx, { ...step.params });
    }
  },

  /**
   * Symmetric to `modifySpellByElement` but targets by action type / range —
   * used by the Lance-Vivante talent (+1 range to ranged physical spells via
   * `step: addSpellRange`).
   */
  modifySpellByActionType: (ctx, p) => {
    const actionType = p.actionType as 'physical' | 'magic' | undefined;
    const minRange = (p.minRange as number | undefined) ?? 0;
    const step = (p as { step?: SpellEffect }).step;
    if (!actionType || !step) {
      return;
    }
    const handler = EFFECT_HANDLERS[step.handler];
    if (!handler) {
      return;
    }
    for (const [spellId, spellDef] of Object.entries(SPELL_REGISTRY)) {
      const dealsType = spellDef.effects?.some(
        (e) =>
          (e.handler === 'dealDamage' || e.handler === 'dealDamageInCells') &&
          (e.params as { type?: string }).type === actionType,
      );
      if (!dealsType) {
        continue;
      }
      if ((spellDef.range ?? 1) < minRange) {
        continue;
      }
      const subCtx = {
        caster: ctx.caster,
        direction: 0,
        affectedCells: [],
        targetSpellId: spellId,
      };
      handler(subCtx, { ...step.params });
    }
  },

  /**
   * Damages entities adjacent to `affectedCells` (radius `radius`, default 1),
   * excluding already-targeted cells. Used by the Tempête talent (lightning
   * spell rebound onto 1 neighboring enemy) — `maxTargets` caps the number of
   * targets hit, `targetTeam` filters by team.
   */
  dealDamageInNeighborsCells: (ctx, p) => {
    const amount = num(p, 'amount');
    const damageType = p.type as 'physical' | 'magic';
    const element = p.element as Element | undefined;
    const radius = (p.radius as number | undefined) ?? 1;
    const maxTargets = p.maxTargets as number | undefined;
    const targetTeam = (p.targetTeam ?? 'opposite') as
      | 'opposite'
      | 'same'
      | 'all';

    const visited = new Set<string>();
    for (const cell of ctx.affectedCells) {
      visited.add(`${cell.x},${cell.y}`);
    }

    let hits = 0;
    for (const cell of ctx.affectedCells) {
      if (maxTargets !== undefined && hits >= maxTargets) {
        break;
      }
      for (let dx = -radius; dx <= radius; dx++) {
        for (let dy = -radius; dy <= radius; dy++) {
          if (maxTargets !== undefined && hits >= maxTargets) {
            break;
          }
          if (dx === 0 && dy === 0) {
            continue;
          }
          const nx = cell.x + dx;
          const ny = cell.y + dy;
          const key = `${nx},${ny}`;
          if (visited.has(key)) {
            continue;
          }
          visited.add(key);
          const entity = dungeonService.getEntityAt(nx, ny, ctx.caster.id);
          if (!entity) {
            continue;
          }
          if (targetTeam === 'opposite' && entity.type === ctx.caster.type) {
            continue;
          }
          if (targetTeam === 'same' && entity.type !== ctx.caster.type) {
            continue;
          }
          entity.lastDamageSourceSpellId = ctx.spell?.id;
          entity.lastDamageSourceCasterId = ctx.caster.id;
          if (damageType === 'physical') {
            ctx.caster.dealPhysicalDamage(entity, amount, element);
          } else {
            ctx.caster.dealMagicDamage(entity, amount, element);
          }
          hits++;
        }
      }
    }
  },

  // see docs/modifiers.md — boost drop pipeline
  spawnBoost: (ctx, p) => {
    const itemId = rollBoostItemId(p.source as string | undefined);
    if (!itemId) {
      return;
    }
    const x = ctx.targetX ?? ctx.caster.positionX;
    const y = ctx.targetY ?? ctx.caster.positionY;
    dungeonService.inventoryService.dropItem(x, y, itemId, 1);
    const player = dungeonService.getPlayer();
    if (player) {
      dungeonService.inventoryService.pickupItemsInRange(
        player,
        player.stats.pickupReach,
      );
    }
  },

  spawnChallengeWave: (ctx, p) => {
    const count = num(p, 'count');
    const triggerLocation = (
      ctx as import('../types/location.types').LocationEffectContext
    ).location;
    if (!triggerLocation) {
      return;
    }

    const room = mapService.getRooms()[triggerLocation.roomIndex];
    if (!room) {
      return;
    }

    const biome = dungeonService.getCurrentBiome();
    if (!biome) {
      return;
    }

    // Seal before spawning: the openings become walls, so the in-room spawn
    // (which only picks ground cells) can never place a monster on a door tile
    // it could then walk out through.
    const openings = mapService.getRoomPerimeterOpenings(room);
    if (openings.length > 0) {
      dynamicPlacementService.requestTileChange(openings, 'wall');
    }

    const spawnedIds: string[] = [];

    for (let i = 0; i < count; i++) {
      const monsterId =
        dungeonService.monsterSpawningService.pickRandomMonsterIdForBiome(
          biome,
        );
      if (!monsterId) {
        continue;
      }
      const pos =
        dungeonService.monsterSpawningService.findSpawnPositionInRoom(room);
      if (!pos) {
        continue;
      }
      const monster = dungeonService.monsterSpawningService.createMonster(
        monsterId,
        pos.x,
        pos.y,
        dungeonService.monsterSpawningService.rollMonsterLevel(),
      );
      if (monster) {
        dungeonService.addEntity(monster);
        spawnedIds.push(monster.id);
      }
    }

    // Nothing spawned: reopen so the room never stays sealed without a wave.
    if (spawnedIds.length === 0) {
      if (openings.length > 0) {
        dynamicPlacementService.requestTileChange(openings, 'restore');
      }
      return;
    }

    const rewardLocation: import('../types/map.types').PlacedLocation = {
      id: LOCATION_CHALLENGE_CHEST.id,
      x: triggerLocation.x,
      y: triggerLocation.y + 3,
      width: LOCATION_CHALLENGE_CHEST.width,
      height: LOCATION_CHALLENGE_CHEST.height,
      placement: LOCATION_CHALLENGE_CHEST.placement,
      roomIndex: triggerLocation.roomIndex,
      sprite: LOCATION_CHALLENGE_CHEST.sprite,
      interactionType: LOCATION_CHALLENGE_CHEST.interactionType,
    };

    challengeService.register(
      triggerLocation,
      spawnedIds,
      rewardLocation,
      triggerLocation.roomIndex,
      (reward) => {
        dynamicPlacementService.requestLocationSpawn(reward);
      },
      () => {
        if (openings.length > 0) {
          dynamicPlacementService.requestTileChange(openings, 'restore');
        }
      },
    );
  },
};
